/**
 * Contact form component — submits to Firebase Cloud Function
 * which creates a Gorgias ticket (instead of Shopify's native contact handler).
 */
class ContactForm extends HTMLElement {
  static MAX_FILES = 3;
  static MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
  static ALLOWED_EXTENSIONS = ['pdf', 'jpeg', 'jpg', 'png'];

  connectedCallback () {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.endpoint = this.getAttribute('data-endpoint') || 'https://us-central1-percko-website.cloudfunctions.net/contactCreateTicket';
    this.successMessage = this.getAttribute('data-success') || 'Votre message a bien été envoyé.';
    this.errorMessage = this.getAttribute('data-error') || 'Une erreur est survenue. Veuillez réessayer.';

    this.fileInput = this.form.querySelector('input[type="file"]');
    this.fileError = this.querySelector('.contact-form__file-error');

    if (this.fileInput) {
      this.fileInput.addEventListener('change', this.validateFiles.bind(this));
    }

    this.initConditionalFields();
    this.initStarRatings();
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  initStarRatings() {
    this.querySelectorAll('[data-star-input]').forEach((container) => {
      const hiddenInput = container.querySelector('input[type="hidden"]');
      const stars = container.querySelectorAll('.contact-form__star');

      stars.forEach((star) => {
        star.addEventListener('click', () => {
          const value = parseInt(star.getAttribute('data-value'), 10);
          hiddenInput.value = value;

          stars.forEach((s) => {
            const v = parseInt(s.getAttribute('data-value'), 10);
            s.classList.toggle('contact-form__star--active', v <= value);
          });
        });
      });
    });
  }

  /**
   * Show/hide fields based on radio selection.
   * Blocks with data-show-when-field="fieldName" data-show-when-value="value"
   * are only visible when the radio with name="fieldName" has value="value" selected.
   */
  initConditionalFields () {
    this.conditionalBlocks = this.querySelectorAll('[data-show-when-field]');
    if (!this.conditionalBlocks.length) return;

    this.form.addEventListener('change', (e) => {
      if (e.target.type === 'radio' || e.target.tagName === 'SELECT') {
        this.updateConditionalFields();
      }
    });

    this.updateConditionalFields();
  }

  updateConditionalFields () {
    this.conditionalBlocks.forEach((block) => {
      const fieldName = block.getAttribute('data-show-when-field');
      const fieldValue = block.getAttribute('data-show-when-value');

      // Support both radio inputs and select elements
      const radio = this.form.querySelector(`input[name="${fieldName}"]:checked`);
      const select = this.form.querySelector(`select[name="${fieldName}"]`);
      const currentValue = radio ? radio.value : (select ? select.value : '');

      if (currentValue === fieldValue) {
        block.style.display = '';
        block.querySelectorAll('input, select, textarea').forEach((el) => {
          el.removeAttribute('disabled');
          el.setAttribute('required', '');
        });
      } else {
        block.style.display = 'none';
        block.querySelectorAll('input, select, textarea').forEach((el) => {
          el.setAttribute('disabled', '');
          el.removeAttribute('required');
        });
      }
    });
  }

  validateFiles () {
    if (!this.fileInput || !this.fileError) return true;

    this.fileError.textContent = '';
    const files = Array.from(this.fileInput.files);

    if (files.length > ContactForm.MAX_FILES) {
      this.fileError.textContent = 'Vous pouvez télécharger un maximum de 3 fichiers.';
      this.fileInput.value = '';
      return false;
    }

    for (const file of files) {
      if (file.size > ContactForm.MAX_FILE_SIZE) {
        this.fileError.textContent = 'La taille de chaque fichier doit être inférieure à 5 Mo.';
        this.fileInput.value = '';
        return false;
      }

      const ext = file.name.split('.').pop().toLowerCase();
      if (!ContactForm.ALLOWED_EXTENSIONS.includes(ext)) {
        this.fileError.textContent = `Veuillez télécharger un fichier ${ContactForm.ALLOWED_EXTENSIONS.join(', ')}`;
        this.fileInput.value = '';
        return false;
      }
    }

    return true;
  }

  async handleSubmit (event) {
    event.preventDefault();

    if (!this.validateFiles()) return;

    const submitButton = this.form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.setAttribute('disabled', '');
      submitButton.setAttribute('aria-busy', 'true');
    }

    this.clearBanners();

    try {
      const formData = this.buildFormData();
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      let result;
      try {
        result = await response.json();
      } catch {
        result = { message: 'Non-JSON response from server' };
      }

      if (response.ok) {
        this.showBanner('success', this.successMessage);
        this.form.reset();
      } else {
        this.showBanner('error', result.message || result.error || this.errorMessage);
      }
    } catch (err) {
      console.error('Contact form submit error:', err);
      this.showBanner('error', this.errorMessage);
    } finally {
      if (submitButton) {
        submitButton.removeAttribute('disabled');
        submitButton.removeAttribute('aria-busy');
      }
    }
  }

  buildFormData () {
    const fd = new FormData();
    const inputs = this.form.querySelectorAll('input, select, textarea');

    inputs.forEach((input) => {
      if (!input.name) return;

      if (input.type === 'file') {
        Array.from(input.files).forEach((file) => {
          fd.append('files', file);
        });
      } else if (input.type === 'radio') {
        if (input.checked) fd.append(input.name, input.value);
      } else if (input.type === 'checkbox') {
        if (input.checked) fd.append(input.name, input.value);
      } else {
        fd.append(input.name, input.value);
      }
    });

    return fd;
  }

  clearBanners () {
    const existing = this.querySelectorAll('.contact-form__banner');
    existing.forEach((el) => el.remove());
  }

  showBanner (status, message) {
    const banner = document.createElement('div');
    banner.className = `contact-form__banner contact-form__banner--${status}`;
    banner.setAttribute('role', status === 'error' ? 'alert' : 'status');
    banner.textContent = message;

    this.form.insertAdjacentElement('beforebegin', banner);
    banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactForm);
}